import { DataSource } from '@client/core/data/concerns/data-source';
import { fromJson, JsonResponse, Mutable, SnakeCaseObject } from '@client/core/data/concerns/parses-from-json';
import { dateToYYYYMMDD } from '@client/core/date/date-to-yyyymmdd';
import { HttpResponse } from '@client/core/http/types';
import { snakeCaseKeys } from '@client/core/utils/snake-case-keys';
import { Comp } from '@client/features/comps/models/comp';
import { Project } from '@client/features/project/models/project';
import { RecommendedCompsSearchSpecs, SearchCriteria } from '@client/features/comps/types';
import { toEnum } from '@client/core/data/concerns/parses-from-json/to-enum';

export enum RecommendedCompsResultCode {
  COMPS_FOUND = 0,
  UNKNOWN_ERROR = 1,
  ALREADY_FETCHED = 2,
  PROJECT_MISSING_COORDINATES = 3,
  NO_RECOMMENDED_COMPS_FOUND = 4,
}

export interface RecommendedCompsResult {
  compsFound: boolean;
  code: RecommendedCompsResultCode;
  message: string;
}

export class CompDataSource extends DataSource {
  /**
   * @see App\Http\Controllers\Comp\GetProjectCompsController
   */
  async findAll(ownerId: number, projectId: Project['uuid']): Promise<Comp[]> {
    const response = await this.httpClient.get<HttpResponse<JsonResponse[]>>(
      `/workspace/${ownerId}/project/${projectId}/comps`,
    );

    return response.data.data.map((comp) => fromJson(Comp, comp));
  }

  /**
   * @see App\Http\Controllers\Comp\StoreCompController
   */
  async store(ownerId: number, projectId: Project['uuid'], data: Mutable<Comp>): Promise<Comp> {
    const response = await this.httpClient.post<HttpResponse<JsonResponse>>(
      `/workspace/${ownerId}/project/${projectId}/comps`,
      compToRequest(data),
    );

    return fromJson(Comp, response.data.data);
  }

  /**
   * @see App\Http\Controllers\Comp\UpdateCompController
   */
  async update(ownerId: number, projectId: Project['uuid'], comp: Mutable<Comp>): Promise<Comp> {
    const response = await this.httpClient.patch<HttpResponse<JsonResponse>>(
      `/workspace/${ownerId}/project/${projectId}/comps/${comp.id}`,
      compToRequest(comp),
    );

    return fromJson(Comp, response.data.data);
  }

  /**
   * @see App\Http\Controllers\Comp\DestroyCompController
   */
  async destroy(ownerId: number, projectId: Project['uuid'], comp: Comp): Promise<void> {
    await this.httpClient.delete<HttpResponse<void>>(`/workspace/${ownerId}/project/${projectId}/comps/${comp.id}`);
  }

  /**
   * @see App\Http\Controllers\Comp\ReorderCompsController
   * @deprecated This feature has been removed from Comps App, anticipating Users no longer need it. If that proves true, delete this functionality.
   */
  async reorderComps(ownerId: number, projectId: Project['uuid'], comps: Comp[]): Promise<void> {
    await this.httpClient.patch<HttpResponse<JsonResponse>>(
      `/workspace/${ownerId}/project/${projectId}/reorder-comps`,
      comps.map((comp) => ({
        id: comp.id,
      })),
    );
  }

  /**
   * @see App\Http\Controllers\Comp\FindRecommendedCompsController
   */
  async findRecommendedComps(
    projectId: Project['uuid'],
    specs: RecommendedCompsSearchSpecs,
  ): Promise<RecommendedCompsResult> {
    const uri = `/project/${projectId}/comps/recommended`;
    const payload = {
      specs,
    };

    const response = await this.httpClient.post<HttpResponse<Record<string, any>>>(uri, payload);

    return {
      compsFound: response.data.data.compsFound,
      code: toEnum(RecommendedCompsResultCode, response.data.data.code),
      message: response.data.data.message,
    };
  }

  /**
   * @see App\Http\Controllers\Comp\FetchCompsForPropertyController
   */
  async searchForComps(ownerId: number, projectId: Project['uuid'], search: SearchCriteria): Promise<void> {
    const payload = { ...search };

    await this.httpClient.post<HttpResponse<void>>(`/workspace/${ownerId}/project/${projectId}/comps-search`, payload);
  }
}

/**
 * Convert a model to a snake_case request.
 * Our API accepts the models in snake_case keys, so this parses the model
 * to snake_case and additionally converts the date to a YYYY-MM-DD string.
 */
function compToRequest(comp: Mutable<Comp>): SnakeCaseObject<Comp> {
  return {
    ...snakeCaseKeys(comp),
    address_1: comp.address1,
    address_2: comp.address2,
    sold_date: comp.soldDate ? dateToYYYYMMDD(comp.soldDate) : null,
  };
}
